import { ComponentPropsWithoutRef, ForwardedRef, forwardRef, useCallback, useContext } from 'react';
import { useInView } from 'react-intersection-observer';
import { useForwardedRef } from '@givebutter/react-util';
import { TableConstants } from 'components/table/TableConstants';
import { TableContext, TableHeaderContext } from 'components/table/TableContext';
import { useResizeObserver } from 'customHooks/useResizeObserver';
import { twMerge } from 'tailwind-merge';

interface TableHeaderDataProps extends ComponentPropsWithoutRef<'th'> {}

const IN_VIEW_ACTION_COLUMN_Y_HIT_SLOP = 5000;

const TableHeaderData = forwardRef(
    (props: TableHeaderDataProps, ref: ForwardedRef<HTMLTableCellElement>) => {
        const internalRef = useForwardedRef(ref);
        const { className = '', ...rest } = props;
        const { variant, setIsActionHidden } = useContext(TableContext);
        const { header } = useContext(TableHeaderContext);
        const variantClass =
            variant === 'bordered'
                ? 'border-b border-b-gray-100 py-2 first:-ml-4 first:pl-6 last:pr-6'
                : 'pt-2 pb-4';

        const { ref: inViewRef } = useInView({
            rootMargin: `${IN_VIEW_ACTION_COLUMN_Y_HIT_SLOP}px 0px`,
            threshold: 0.5,
            onChange: inView => {
                if (header.column.id === TableConstants.Actions) {
                    setIsActionHidden(!inView);
                }
            },
        });

        useResizeObserver(internalRef.current, entry => {
            const table = entry.target.closest('table');
            const headers = table?.querySelectorAll('th') ?? [];
            let found = false;
            let offset = 0;
            headers.forEach(th => {
                if (th === entry.target) {
                    found = true;
                }

                if (!found) {
                    offset += th.clientWidth;
                }
            });

            table?.style.setProperty(
                `${TableConstants.ColumnOffsetPropertyPrefix}-${header.id}`,
                `${offset.toString()}px`,
            );
        });

        const setRefs = useCallback(
            (node: HTMLElement | null) => {
                internalRef.current = node as HTMLTableCellElement;
                inViewRef(node);
            },
            [inViewRef, internalRef],
        );

        return (
            <th
                ref={setRefs}
                className={twMerge(variantClass, className, `px-4 text-start`)}
                {...rest}
            />
        );
    },
);

export { TableHeaderData };
export type { TableHeaderDataProps };
